import {
  Gateway,
  deriveWireClientId,
  deriveWireUserId,
  decodeJwtSub,
  type AggregateChange,
  type AggregateClient,
  type AuthProvider,
  type RetryPolicy,
  type ShareOptions,
  type ShareResult,
} from '@celeriant/wire';
import type {
  AggregateDef,
  AggregateKind,
  AggregatePayload,
  AggregateResult,
  AggregateState,
} from './aggregate.js';
import { SyncEngine, type DeletedReason, type EngineCryptoProvider } from './engine.js';
import type { Unsubscribe } from './notifier.js';
import { OnlineEngine, type ExecuteResult } from './onlineEngine.js';
import { RealtimeMultiplexer } from './realtime.js';
import { SendScheduler } from './scheduler.js';
import type { SyncStatus } from './status.js';
import { assert } from './assert.js';
import { Store } from './store.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDef = AggregateDef<any, any>;

export interface PersistentHandle<TDef extends AnyDef> {
  readonly id: string;
  readonly state: AggregateState<TDef>;
  commit<K extends AggregateKind<TDef>>(kind: K, payload: AggregatePayload<TDef, K>): Promise<void>;
  // One call per unit of work, carrying every projection result that unit produced. Never one
  // per event: a store bound to this sees one render at the end of a batch.
  subscribe(listener: (state: AggregateState<TDef>, results: readonly AggregateResult<TDef>[]) => void): Unsubscribe;
  undo(): Promise<boolean>;
  redo(): Promise<boolean>;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  // Latches once an incoming batch was refused as unfoldable. The aggregate goes READ-ONLY:
  // inbound folds stop at the refused event and commit/undo/redo throw UpdateRequiredError, so a
  // client that cannot fold the latest state cannot emit edits computed against a stale picture.
  // Surface an "update the app" prompt; on reopen the updated build folds everything and drains
  // what is unsent.
  readonly updateRequired: boolean;
  // An optional shareIdHash authorises a redeemer holding no owner grant to read by share code.
  pull(opts?: { shareIdHash?: string }): Promise<void>;
  send(): Promise<void>;
  // Sync health for the UI: draw the spinner and the offline badge from here, the data from
  // `state`. `subscribeStatus` fires on every change, field-diffed.
  readonly status: SyncStatus;
  subscribeStatus(listener: (status: SyncStatus) => void): Unsubscribe;
  // Opened with `{ syncGate: 'closed' }` the aggregate makes ZERO network contact, so a private
  // local-only aggregate never announces itself to the gateway. `openSyncGate()` opens it (on
  // share, login, or opt-in): it seeds, catch-up pulls, subscribes if realtime, and drains what
  // queued while closed. Idempotent.
  readonly syncGate: 'open' | 'closed';
  openSyncGate(opts?: { shareIdHash?: string }): Promise<void>;
  // Mints a share code. The returned `shareKey` travels out of band; the redeeming device calls
  // `Celeriant.redeemShare` with it. Owner-only at the gateway. The mint is network contact, so
  // on a gate-closed aggregate it is the moment privacy ends by intent: the gate does not
  // suppress it.
  share(options: ShareOptions): Promise<ShareResult>;
  close(): Promise<void>;
}

// The online handle. Narrower than PersistentHandle: no optimistic commit, no undo, no pending
// count, no sync gate (an online aggregate IS the network). `execute` is the only write.
export interface OnlineHandle<TDef extends AnyDef> {
  readonly id: string;
  // Latest folded server state; it may lag a peer's just-landed write until the next fold.
  readonly state: AggregateState<TDef>;
  readonly version: number;
  readonly updateRequired: boolean;
  // Resolves { version } after the server acks; throws ValidationError if the precondition fails
  // against fresh state, or ConflictError after bounded retries (state is refreshed first, so
  // `.version` on the error is current). `idempotencyKey` must be a canonical UUID, reused on a
  // button-retry so a landed write resolves to a dedup hit. That hit is bounded by the 90s dedup
  // window: a same-key retry after the window goes out with a freshly derived client_seq, above
  // the gateway's seq-gated idempotency check, so it appends a second time.
  // Throws OfflineError immediately, without touching the gateway, when the app is offline at
  // the moment of the call; see CeleriantOptions.failFastWhenOffline to opt out.
  // For an event declared `occ: false` the write carries no version guard, so it resolves with
  // the version it landed at BEFORE that event reaches `state`: it arrives on the next refresh,
  // folded from a read, because only a folded version may move the cursor.
  execute<K extends AggregateKind<TDef>>(
    kind: K, payload: AggregatePayload<TDef, K>, opts?: { idempotencyKey?: string },
  ): Promise<ExecuteResult>;
  refresh(): Promise<void>;
  subscribe(listener: (state: AggregateState<TDef>, results: readonly AggregateResult<TDef>[]) => void): Unsubscribe;
  readonly status: SyncStatus;
  subscribeStatus(listener: (status: SyncStatus) => void): Unsubscribe;
  share(options: ShareOptions): Promise<ShareResult>;
  close(): Promise<void>;
}

// `realtime` (default true) gates the open-time SSE subscribe and nothing else: pass false to
// suppress a subscribe that would be refused anyway, since the gateway's SSE endpoint accepts no
// share_id and an ungranted client gets a 403 the EventSource cannot retry. Call
// `enableRealtime(def, id)` once the grant lands.
export interface AggregateOnlineOptions {
  realtime?: boolean;
}

export interface RedeemShareOptions {
  // The opaque code from `handle.share()`, as scanned or pasted by the redeeming device.
  shareKey: string;
}

export interface CeleriantOptions {
  appName: string;
  gateway: string;
  auth: AuthProvider;
  aggregates: ReadonlyArray<AnyDef>;
  fetch?: typeof globalThis.fetch;
  eventSourceFactory?: ConstructorParameters<typeof Gateway>[0]['eventSourceFactory'];
  retry?: Partial<RetryPolicy>;
  now?: () => number;
  generateClientId?: () => string;
  // Required if any registered aggregate encrypts. Typically a DefaultCryptoProvider from
  // `@celeriant/sdk-encrypt`.
  crypto?: EngineCryptoProvider;
  // Fired after an aggregate's local replica was purged: the server's delete poke, or a pull
  // discovering the stream gone ('deleted') or access revoked ('access-revoked'). The handle is
  // already evicted; the app surfaces the reason and drops its references.
  onDeleted?: (info: { type: string; id: string; reason: DeletedReason }) => void;
  // Fired once per aggregate when an incoming batch is refused because this build cannot fold
  // it. The aggregate's `handle.updateRequired` latches true.
  onUpdateRequired?: (info: { type: string; id: string }) => void;
  // SSE write/create pokes funnel through a trailing throttle of this many ms per aggregate, so
  // N painting peers collapse to about one read per window instead of N reads per second.
  // Default 50ms; 0 pulls on the next tick. trim and delete pokes are never coalesced: they are
  // rare and order-sensitive.
  readCoalesceMs?: number;
  // Online writes fail fast while the app is offline. Default true: execute() rejects with
  // OfflineError at entry, before the catch-up read, instead of spending a doomed read plus a
  // retry ladder to reach the same failure a minute later. Local-first commit() is never gated
  // (its log IS the offline story) and neither is refresh().
  //
  // Safe as a default because the signal is the one the status surface already reports:
  // navigator.onLine === false means no interface at all, and an app running where that signal
  // does not exist never reports offline, so the gate never fires. An OfflineError does not set
  // status.lastError: it is expected state, not a sync failure.
  failFastWhenOffline?: boolean;
  // Sync orchestration: the SDK owns the cadence. Commits send on a short debounce capped by a
  // max wait, so a commit storm cannot starve the send; failed sends retry on a doubling
  // backoff; the browser 'online' event flushes and pulls everything; a backstop interval
  // re-drives send and pull as the last-resort trigger; pagehide and visibilitychange flush
  // best-effort. Zeros and nulls are "off".
  sync?: {
    sendThrottleMs?: number;
    sendMaxWaitMs?: number;
    retryBaseMs?: number;
    retryMaxMs?: number;
    backstopMs?: number | null;
  };
}

const DEFAULT_READ_COALESCE_MS = 50;
const DEFAULT_BACKSTOP_MS = 20_000;

// One per app. Owns the Store (the IDB connection), the Gateway, the registered definitions and
// the handle cache. A consumer does `const c = new Celeriant({...}); await c.ready(); const h =
// await c.aggregate(def, id);` and the handle wires up state, sync and undo.
export class Celeriant {
  private readonly store: Store;
  private readonly gateway: Gateway;
  private readonly defsByName = new Map<string, AnyDef>();
  private readonly handles = new Map<string, PersistentHandle<AnyDef>>();
  // Parallel to `handles`: the SSE dispatch routes changes to engines directly, because pull,
  // trim and delete are engine-level rather than handle-level.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly engines = new Map<string, SyncEngine<any>>();
  // Single-flight: concurrent aggregate() calls for one (def, id) share a build. Without it two
  // callers each construct and open an engine, both write the same Store, and end up with
  // diverging projections of one persisted aggregate.
  private readonly handlesInflight = new Map<string, Promise<PersistentHandle<AnyDef>>>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly onlineEngines = new Map<string, OnlineEngine<any>>();
  private readonly onlineHandles = new Map<string, OnlineHandle<AnyDef>>();
  private readonly onlineInflight = new Map<string, Promise<OnlineHandle<AnyDef>>>();
  private readonly mux: RealtimeMultiplexer;
  private readonly crypto: EngineCryptoProvider | undefined;
  private readonly onDeleted: ((info: { type: string; id: string; reason: DeletedReason }) => void) | undefined;
  private readonly onUpdateRequired: ((info: { type: string; id: string }) => void) | undefined;
  private readonly now: () => number;
  private readonly auth: AuthProvider;
  private readonly readCoalesceMs: number;
  private readonly failFastWhenOffline: boolean;
  private readonly pullTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly scheduler: SendScheduler;
  private readonly backstopMs: number | null;
  private backstopTimer: ReturnType<typeof setInterval> | undefined;
  private readonly domListeners: Array<{ target: 'window' | 'document'; event: string; fn: () => void }> = [];
  // Same-device tab convergence. A local commit posts {type,id}; sibling tabs fold the tail from
  // the shared IDB with no server hop. Browser-only; undefined in Node.
  private broadcast: BroadcastChannel | undefined;
  private readonly appName: string;
  private clientId: string | undefined;
  // The authenticated wire user_id, derived once at ready() from the auth provider's bearer
  // token, threaded into every engine so own events attribute to the real user. Undefined for an
  // anonymous keypair-only client.
  private userId: string | undefined;
  // App-level connectivity, injected into every engine as `() => this.online` and flipped by the
  // DOM listeners. Online by default where the signal does not exist.
  private online: boolean = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true;
  private opened = false;
  private closed = false;

  constructor(options: CeleriantOptions) {
    if ('snapshotEvery' in options) throw new Error('Celeriant: snapshotEvery is gone; there is no snapshot cadence');
    for (const def of options.aggregates) {
      if (this.defsByName.has(def.name)) throw new Error(`Celeriant: duplicate aggregate name "${def.name}"`);
      this.defsByName.set(def.name, def);
    }
    this.appName = options.appName;
    const storeOpts: ConstructorParameters<typeof Store>[0] = { appName: options.appName };
    if (options.generateClientId !== undefined) storeOpts.generateClientId = options.generateClientId;
    this.store = new Store(storeOpts);
    const gatewayOpts: ConstructorParameters<typeof Gateway>[0] = {
      baseUrl: options.gateway,
      auth: options.auth,
    };
    if (options.fetch !== undefined) gatewayOpts.fetch = options.fetch;
    if (options.eventSourceFactory !== undefined) gatewayOpts.eventSourceFactory = options.eventSourceFactory;
    if (options.retry !== undefined) gatewayOpts.retry = options.retry;
    if (options.now !== undefined) gatewayOpts.now = options.now;
    this.gateway = new Gateway(gatewayOpts);
    this.now = options.now ?? Date.now;
    this.auth = options.auth;
    this.readCoalesceMs = options.readCoalesceMs ?? DEFAULT_READ_COALESCE_MS;
    this.failFastWhenOffline = options.failFastWhenOffline ?? true;
    this.crypto = options.crypto;
    this.onDeleted = options.onDeleted;
    this.onUpdateRequired = options.onUpdateRequired;
    this.mux = new RealtimeMultiplexer({
      gateway: this.gateway,
      dispatch: (type, change) => { this.dispatchChange(type, change); },
    });
    const sync = options.sync ?? {};
    this.scheduler = new SendScheduler({
      ...(sync.sendThrottleMs !== undefined ? { throttleMs: sync.sendThrottleMs } : {}),
      ...(sync.sendMaxWaitMs !== undefined ? { maxWaitMs: sync.sendMaxWaitMs } : {}),
      ...(sync.retryBaseMs !== undefined ? { retryBaseMs: sync.retryBaseMs } : {}),
      ...(sync.retryMaxMs !== undefined ? { retryMaxMs: sync.retryMaxMs } : {}),
      ...(options.now !== undefined ? { now: options.now } : {}),
    });
    this.backstopMs = sync.backstopMs === undefined ? DEFAULT_BACKSTOP_MS : sync.backstopMs;
  }

  async ready(): Promise<void> {
    if (this.opened) return;
    await this.store.open();
    // The client_id MUST equal the gateway's principal id, derived from the auth public key, and
    // not a random local id: otherwise a write cannot recognise its own events in a read and the
    // post-wipe seed silently no-ops, dropping writes. The provider is invoked once for the key;
    // the signature it also returns is discarded here.
    const creds = await this.auth(String(this.now()));
    this.clientId = await deriveWireClientId(creds.publicKey);
    // Best-effort: an anonymous client, or a token with no usable sub, leaves userId undefined
    // and attribution falls back to the clientId.
    if (creds.bearerToken !== undefined) {
      const sub = decodeJwtSub(creds.bearerToken);
      if (sub !== undefined) this.userId = await deriveWireUserId(sub);
    }
    this.installSyncTriggers();
    this.opened = true;
  }

  // The ids of a type's aggregates that exist in LOCAL storage on this device: created here, or
  // pulled and opened before. Enough to split a picker into "on this device" and "downloadable
  // from the server". Requires ready() first.
  async localAggregateIds(def: { name: string }): Promise<string[]> {
    const records = await this.store.listRecords(def.name);
    return records.map((r) => r.id);
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    if (this.backstopTimer !== undefined) clearInterval(this.backstopTimer);
    if (this.broadcast !== undefined) { this.broadcast.onmessage = null; this.broadcast.close(); this.broadcast = undefined; }
    for (const l of this.domListeners) {
      (l.target === 'window' ? window : document).removeEventListener(l.event, l.fn);
    }
    this.domListeners.length = 0;
    // Best-effort flush of what is queued, then stop scheduling. A commit landing DURING an
    // in-flight send is not transmitted here; it stays durable in the log and sends on the next
    // open, reconnect or backstop.
    await this.scheduler.flushAll().catch((e: unknown) => { console.error('Celeriant.close: final flush failed:', e); });
    this.scheduler.close();
    await this.mux.close();
    for (const timer of this.pullTimers.values()) clearTimeout(timer);
    this.pullTimers.clear();
    // Snapshot the handles before iterating; each close() removes itself from the map.
    for (const handle of Array.from(this.handles.values())) {
      try { await handle.close(); }
      catch (e: unknown) { console.error('Celeriant.close: handle close failed:', e); }
    }
    this.handles.clear();
    this.engines.clear();
    this.handlesInflight.clear();
    for (const handle of Array.from(this.onlineHandles.values())) {
      try { await handle.close(); }
      catch (e: unknown) { console.error('Celeriant.close: online handle close failed:', e); }
    }
    this.onlineHandles.clear();
    this.onlineEngines.clear();
    this.onlineInflight.clear();
    this.store.close();
  }

  // Opens the SSE subscription for an already-open aggregate of either pattern. Used when an
  // aggregate was opened with `{ realtime: false }`, such as an ungranted share redeemer that
  // must not subscribe before it holds a grant. Idempotent; a no-op if the aggregate is not open.
  enableRealtime<TDef extends AnyDef>(def: TDef, id: string): void {
    const key = engineKey(def.name, id);
    if (this.engines.has(key)) {
      this.maybeSubscribe(def.name, id);
      return;
    }
    // Online aggregates have no sync gate: they ARE the network.
    if (this.onlineEngines.has(key)) this.mux.subscribe(def.name, id);
  }

  // Claims a share code for (def, id): ONE share-scoped read from version 0, which is what turns
  // the code into a durable grant for this principal at the gateway. Resolves on a 200, empty
  // body or not, and surfaces wire errors as they are: an invalid, expired or already-consumed
  // code is Forbidden.
  // NotExist means the redeem granted nothing usable: the aggregate was deleted, or was never
  // created. Whether the code's single use was burned server-side is not knowable from here, so
  // treat it as a failure and do not silently retry with the same code.
  // This contacts the gateway naming (def, id) regardless of any open handle's sync gate:
  // redeeming is the deliberate act of joining a shared aggregate, the same rationale as the
  // mint on handle.share().
  async redeemShare<TDef extends AnyDef>(def: TDef, id: string, options: RedeemShareOptions): Promise<void> {
    if (!this.opened) throw new Error('Celeriant: ready() must be called first');
    if (this.closed) throw new Error('Celeriant: closed');
    this.requireRegistered(def);
    if (id.length === 0) throw new Error('Celeriant: aggregate id must not be empty');
    if (id.includes('\0')) throw new Error('Celeriant: aggregate id must not contain NUL bytes');
    const shareIdHash = await deriveShareIdHash(options.shareKey);
    await this.gateway.aggregate(def.name, id).redeemShare(shareIdHash);
  }

  // Returns a live, persistent handle to one aggregate. Subsequent calls with the same (def, id)
  // return the SAME handle; concurrent calls during construction share one in-flight build.
  async aggregate<TDef extends AnyDef>(
    def: TDef,
    id: string,
    options: { realtime?: boolean; syncGate?: 'open' | 'closed' } = {},
  ): Promise<PersistentHandle<TDef>> {
    if (!this.opened) throw new Error('Celeriant: ready() must be called first');
    if (this.closed) throw new Error('Celeriant: closed');
    this.requireRegistered(def);
    if (id.includes('\0')) throw new Error('Celeriant: aggregate id must not contain NUL bytes');
    const realtime = options.realtime ?? true;
    // 'closed' opens the aggregate with zero network: no subscribe here, and the engine gates
    // send, pull and seed. Applied only on the first build.
    const syncGate = options.syncGate ?? 'open';
    const key = engineKey(def.name, id);
    const existing = this.handles.get(key);
    if (existing !== undefined) {
      // The gate is create-only. Silently returning an OPEN handle to a caller that asked for
      // 'closed' would leak a private aggregate to the gateway, so that direction is loud; the
      // reverse is fine, because openSyncGate() is how a closed handle opts in.
      if (syncGate === 'closed' && existing.syncGate === 'open') {
        console.error(`Celeriant: aggregate "${def.name}/${id}" is already open with the sync gate OPEN; the requested syncGate:'closed' is ignored (the option applies only on first open). It is already contacting the gateway.`);
      }
      if (realtime) this.maybeSubscribe(def.name, id);
      return existing as PersistentHandle<TDef>;
    }
    const inflight = this.handlesInflight.get(key);
    if (inflight !== undefined) {
      // The build in flight carries the FIRST caller's realtime choice. A later caller asking
      // for realtime must still get its stream, or the concurrent ordering would disagree with
      // the sequential one. Through maybeSubscribe, so the gate still has the last word.
      if (realtime) return inflight.then((h) => { this.maybeSubscribe(def.name, id); return h; }) as Promise<PersistentHandle<TDef>>;
      return inflight as Promise<PersistentHandle<TDef>>;
    }

    const promise = (async (): Promise<PersistentHandle<AnyDef>> => {
      const wire = this.gateway.aggregate(def.name, id);
      const engine = new SyncEngine({
        def,
        aggregateId: id,
        store: this.store,
        gateway: wire,
        clientId: this.requireClientId(),
        ...(this.userId !== undefined ? { userId: this.userId } : {}),
        syncGate,
        isOnline: () => this.online,
        now: this.now,
        ...(this.crypto !== undefined ? { crypto: this.crypto } : {}),
        // A delete poke or a purge on pull evicts the cached handle, so a re-open builds a fresh
        // engine. Without it the deleted handle's record is reset in place and the next commit
        // recycles client_seq from 1 into the server's residual dedup state.
        onDeleted: (reason) => {
          this.handles.delete(key);
          this.engines.delete(key);
          this.mux.unsubscribe(def.name, id);
          this.scheduler.cancel(key);
          this.onDeleted?.({ type: def.name, id, reason });
        },
        onLocalCommit: () => {
          this.scheduler.schedule(key, () => engine.send());
          this.broadcast?.postMessage({ type: def.name, id });
        },
        ...(this.onUpdateRequired !== undefined
          ? { onUpdateRequired: (): void => { this.onUpdateRequired!({ type: def.name, id }); } }
          : {}),
      });
      await engine.open();
      // Drain anything a previous session left unsent.
      this.scheduler.schedule(key, () => engine.send());
      const handle = makeHandle(id, engine, wire, () => {
        this.handles.delete(key);
        this.engines.delete(key);
        this.mux.unsubscribe(def.name, id);
        this.scheduler.cancel(key);
      }, () => {
        // After the engine opens its gate (seed plus catch-up pull), start the stream if this
        // aggregate wanted realtime and drain what queued while it was closed.
        if (realtime) this.maybeSubscribe(def.name, id);
        this.scheduler.schedule(key, () => engine.send());
      });
      this.engines.set(key, engine);
      if (realtime) this.maybeSubscribe(def.name, id);
      return handle as PersistentHandle<AnyDef>;
    })();
    this.handlesInflight.set(key, promise);
    try {
      const handle = await promise;
      this.handles.set(key, handle);
      return handle as PersistentHandle<TDef>;
    } finally {
      this.handlesInflight.delete(key);
    }
  }

  // Returns a live OnlineHandle for a mode:'online' aggregate. A separate method from aggregate()
  // so each handle keeps clean, non-union types. Same (def, id) returns the SAME handle;
  // concurrent builds share one in-flight promise.
  async aggregateOnline<TDef extends AnyDef>(
    def: TDef,
    id: string,
    options: AggregateOnlineOptions = {},
  ): Promise<OnlineHandle<TDef>> {
    if (!this.opened) throw new Error('Celeriant: ready() must be called first');
    if (this.closed) throw new Error('Celeriant: closed');
    this.requireRegistered(def);
    if (def.mode !== 'online') {
      throw new Error(`Celeriant: aggregate "${def.name}" is not mode:'online'; use aggregate() for local-first`);
    }
    if (id.includes('\0')) throw new Error('Celeriant: aggregate id must not contain NUL bytes');
    const realtime = options.realtime ?? true;
    const key = engineKey(def.name, id);
    const existing = this.onlineHandles.get(key);
    if (existing !== undefined) {
      // A cached handle first opened with realtime:false starts its stream when a later open
      // asks for realtime; the mux ignores a repeat subscribe.
      if (realtime) this.mux.subscribe(def.name, id);
      return existing as OnlineHandle<TDef>;
    }
    const inflight = this.onlineInflight.get(key);
    if (inflight !== undefined) {
      if (realtime) return inflight.then((h) => { this.mux.subscribe(def.name, id); return h; }) as Promise<OnlineHandle<TDef>>;
      return inflight as Promise<OnlineHandle<TDef>>;
    }

    const promise = (async (): Promise<OnlineHandle<AnyDef>> => {
      const wire = this.gateway.aggregate(def.name, id);
      const engine = new OnlineEngine({
        def,
        aggregateId: id,
        store: this.store,
        gateway: wire,
        clientId: this.requireClientId(),
        ...(this.userId !== undefined ? { userId: this.userId } : {}),
        now: this.now,
        isOnline: () => this.online,
        failFastWhenOffline: this.failFastWhenOffline,
        ...(this.crypto !== undefined ? { crypto: this.crypto } : {}),
        ...(this.onUpdateRequired !== undefined
          ? { onUpdateRequired: (): void => { this.onUpdateRequired!({ type: def.name, id }); } }
          : {}),
      });
      await engine.open();
      const handle = makeOnlineHandle(id, engine, wire, () => {
        this.onlineHandles.delete(key);
        this.onlineEngines.delete(key);
        this.mux.unsubscribe(def.name, id);
      });
      this.onlineEngines.set(key, engine);
      if (realtime) this.mux.subscribe(def.name, id);
      return handle as OnlineHandle<AnyDef>;
    })();
    this.onlineInflight.set(key, promise);
    try {
      const handle = await promise;
      this.onlineHandles.set(key, handle);
      return handle as OnlineHandle<TDef>;
    } finally {
      this.onlineInflight.delete(key);
    }
  }

  // Internals ---------------------------------------------------------------------------------

  private requireRegistered(def: AnyDef): void {
    if (this.defsByName.get(def.name) !== def) {
      throw new Error(`Celeriant: aggregate "${def.name}" was not registered in the constructor`);
    }
  }

  // The remaining sync triggers, installed at ready(): reconnect flushes every pending send and
  // pulls every open aggregate; the backstop interval re-drives send and pull as the last resort
  // (a missed poke, a dropped timer); pagehide and visibilitychange flush best-effort, because
  // the page may be going away. All three are browser-only; in Node the explicit send() and
  // pull() calls remain the drivers.
  private installSyncTriggers(): void {
    this.backstopTimer = this.backstopMs === null
      ? undefined
      : setInterval(() => { this.driveAll(); }, this.backstopMs);
    // A tab does not receive its own posts; a sibling's {type,id} folds the tail from the shared
    // IDB. Browser-only.
    if (typeof BroadcastChannel === 'function') {
      this.broadcast = new BroadcastChannel(`celeriant:${this.appName}`);
      this.broadcast.onmessage = (ev: MessageEvent): void => {
        const msg = ev.data as { type?: string; id?: string } | null;
        if (msg?.type === undefined || msg.id === undefined) return;
        const engine = this.engines.get(engineKey(msg.type, msg.id));
        void engine?.refreshFromStore().catch((e: unknown) => { console.error('Celeriant: cross-tab refresh failed:', e); });
      };
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      const onOnline = (): void => { this.setOnline(true); this.driveAll(); };
      window.addEventListener('online', onOnline);
      this.domListeners.push({ target: 'window', event: 'online', fn: onOnline });
      const onOffline = (): void => { this.setOnline(false); };
      window.addEventListener('offline', onOffline);
      this.domListeners.push({ target: 'window', event: 'offline', fn: onOffline });
      const onPagehide = (): void => { void this.scheduler.flushAll(); };
      window.addEventListener('pagehide', onPagehide);
      this.domListeners.push({ target: 'window', event: 'pagehide', fn: onPagehide });
    }
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      const onVisibility = (): void => {
        if (document.visibilityState === 'hidden') void this.scheduler.flushAll();
      };
      document.addEventListener('visibilitychange', onVisibility);
      this.domListeners.push({ target: 'document', event: 'visibilitychange', fn: onVisibility });
    }
  }

  // Each engine's `online` reads `() => this.online`, so it only needs a nudge to re-emit.
  private setOnline(value: boolean): void {
    if (this.online === value) return;
    this.online = value;
    for (const engine of this.engines.values()) engine.notifyConnectivityChanged();
    for (const engine of this.onlineEngines.values()) engine.notifyConnectivityChanged();
  }

  // Send and pull every open aggregate. Sends flush through the scheduler, sharing its in-flight
  // coalescing; pulls go direct, because they re-derive from the cursor and a redundant pull is
  // one cheap request. Errors are logged, never thrown: this runs from timers and DOM events
  // with nobody to catch.
  private driveAll(): void {
    if (this.closed) return;
    void this.scheduler.flushAll().catch((e: unknown) => { console.error('Celeriant: flushAll failed:', e); });
    for (const [key, engine] of this.engines) {
      void engine.send().catch(() => { /* the scheduler's retry owns send failures */ });
      // backgroundPull, not pull: a blind plain pull against a share-scoped engine 403s and
      // wrongly purges it. backgroundPull reuses the engine's known scope.
      void engine.backgroundPull().catch((e: unknown) => { console.error(`Celeriant: backstop pull failed for ${key}:`, e); });
    }
    for (const [key, engine] of this.onlineEngines) {
      void engine.refresh().catch((e: unknown) => { console.error(`Celeriant: backstop refresh failed for ${key}:`, e); });
    }
  }

  // SSE-driven side effects. Lookup is best-effort: a change for an aggregate this process never
  // opened is dropped, because the next aggregate() call reads fresh state anyway. There is no
  // `default:` arm that reads: an unknown operation is a forward-compatible one, and turning it
  // into a spurious full read is the bug this shape exists to avoid.
  private dispatchChange(type: string, change: AggregateChange): void {
    const key = engineKey(type, change.aggregateId);
    const engine = this.engines.get(key);
    if (engine !== undefined) {
      // Write pokes coalesce here because the root owns the window; the engine's switch handles
      // the rest and ignores what it does not know.
      if (change.operation === 'write' || change.operation === 'create') {
        this.coalesceDrive(key, () => engine.pull());
        return;
      }
      void engine.applyChange(change).catch((e: unknown) => { console.error(`Celeriant: ${change.operation} after poke failed:`, e); });
      return;
    }
    // Every poke on an online aggregate is one coalesced catch-up read: it has no optimistic
    // state to rewind and no local trim machinery, and a server delete surfaces as NotExist on
    // the next read.
    const online = this.onlineEngines.get(key);
    if (online !== undefined) this.coalesceDrive(key, () => online.refresh());
  }

  // Trailing throttle: the first poke in a quiet period schedules the drive readCoalesceMs
  // later, and pokes arriving before it fires are absorbed, because the drive re-derives from
  // the cursor and covers them. A new window opens only after it fires.
  // Identity is established by ready(); every aggregate() path runs after it.
  private requireClientId(): string {
    assert(this.clientId !== undefined, 'Celeriant: ready() has not run, so there is no client identity to open an aggregate under');
    assert(this.clientId.length > 0, 'Celeriant: the client identity is set');
    return this.clientId;
  }

  private coalesceDrive(key: string, drive: () => Promise<void>): void {
    if (this.pullTimers.has(key)) return;
    const timer = setTimeout(() => {
      this.pullTimers.delete(key);
      void drive().catch((e: unknown) => { console.error('Celeriant: catch-up after poke failed:', e); });
    }, this.readCoalesceMs);
    this.pullTimers.set(key, timer);
  }

  // Every subscribe site routes through here, so an aggregate whose gate is closed never opens
  // an SSE stream. openSyncGate() flips the gate first, then re-subscribes.
  private maybeSubscribe(type: string, id: string): void {
    if (this.engines.get(engineKey(type, id))?.syncGate === 'closed') return;
    this.mux.subscribe(type, id);
  }
}

function engineKey(type: string, id: string): string {
  return `${type}\0${id}`;
}

function makeOnlineHandle<TDef extends AnyDef>(
  id: string,
  engine: OnlineEngine<AggregateState<TDef>>,
  wire: AggregateClient,
  onClose: () => void,
): OnlineHandle<TDef> {
  // share() is the one handle method that talks to the gateway without going through the engine,
  // so it needs its own lifecycle flag: the engine's `closed` guard cannot cover it.
  let closed = false;
  return {
    id,
    get state(): AggregateState<TDef> { return engine.state as AggregateState<TDef>; },
    get version(): number { return engine.version; },
    get updateRequired(): boolean { return engine.updateRequired; },
    execute(kind, payload, opts) {
      return engine.execute(kind as string, payload, opts ?? {});
    },
    refresh() { return engine.refresh(); },
    subscribe(listener) {
      return engine.subscribe((s, results) => { listener(s as AggregateState<TDef>, results as readonly AggregateResult<TDef>[]); });
    },
    get status(): SyncStatus { return engine.statusValue; },
    subscribeStatus(listener) { return engine.subscribeStatus(listener); },
    async share(options) {
      if (closed) throw new Error('Celeriant: handle is closed');
      return wire.share(options);
    },
    async close() {
      closed = true;
      await engine.close();
      onClose();
    },
  };
}

function makeHandle<TDef extends AnyDef>(
  id: string,
  engine: SyncEngine<AggregateState<TDef>>,
  wire: AggregateClient,
  onClose: () => void,
  onOpenSyncGate: () => void,
): PersistentHandle<TDef> {
  let closed = false;
  return {
    id,
    get state(): AggregateState<TDef> { return engine.state as AggregateState<TDef>; },
    async commit(kind, payload) {
      await engine.commitLocal({ kind, payload });
    },
    subscribe(listener) {
      return engine.subscribe((s, results) => { listener(s as AggregateState<TDef>, results as readonly AggregateResult<TDef>[]); });
    },
    undo() { return engine.undo(); },
    redo() { return engine.redo(); },
    get canUndo(): boolean { return engine.canUndo; },
    get canRedo(): boolean { return engine.canRedo; },
    get updateRequired(): boolean { return engine.updateRequired; },
    pull(opts) { return engine.pull(opts ?? {}); },
    send() { return engine.send(); },
    get status(): SyncStatus { return engine.statusValue; },
    subscribeStatus(listener) { return engine.subscribeStatus(listener); },
    get syncGate(): 'open' | 'closed' { return engine.syncGate; },
    async openSyncGate(opts) {
      await engine.openSyncGate(opts);
      onOpenSyncGate();
    },
    async share(options) {
      if (closed) throw new Error('Celeriant: handle is closed');
      return wire.share(options);
    },
    async close() {
      closed = true;
      await engine.close();
      onClose();
    },
  };
}

// The `share_id` a redeemer presents on read: sha256(utf8(shareKey))[..16] as STANDARD base64,
// the same transform as the client_id derivation, over the share key. The wire layer owns the
// conversion to the URL-safe no-pad form the gateway's query parameters take.
async function deriveShareIdHash(shareKey: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(shareKey)));
  let bin = '';
  for (let i = 0; i < 16; i += 1) bin += String.fromCharCode(digest[i]!);
  return btoa(bin);
}
